<div class="site-form-container">
  <!-- Loading Spinner -->
  @if (saving) {
    <div class="loading-overlay">
      <mat-spinner diameter="60"></mat-spinner>
      <p>Saving site...</p>
    </div>
  }

  <!-- Header Section -->
  <div class="header-section">
    <div class="title-section">
      <h1>{{ isEditMode ? 'Edit Site' : 'New Site' }}</h1>
      <p class="subtitle">
        {{ isEditMode ? 'Update site information' : 'Create a new site record' }}
        @if (currentSite.inactive) {
          <span class="inactive-badge">(INACTIVE)</span>
        }
      </p>
    </div>
    
    <div class="header-actions">
      <button mat-stroked-button color="accent" (click)="cancelSubmit()">
        <mat-icon>close</mat-icon>
        Cancel
      </button>
      <button mat-raised-button color="primary" 
              [disabled]="!canSave || !isFormValid() || saving"
              (click)="onSubmit()">
        <mat-icon>save</mat-icon>
        {{ isEditMode ? 'Update Site' : 'Save Site' }}
      </button>
    </div>
  </div>

  <form [formGroup]="form" (ngSubmit)="onSubmit()">
    
    <!-- Main Site Information Card -->
    <mat-card class="info-card">
      <mat-card-header>
        <mat-card-title>
          <mat-icon>location_on</mat-icon>
          Site Information
        </mat-card-title>
      </mat-card-header>
      <mat-card-content>
        <div class="form-grid site-grid">
          
          <!-- Site Number (Read-only) -->
          <mat-form-field appearance="outline">
            <mat-label>Site Number</mat-label>
            <input matInput formControlName="siteNum" readonly>
            <mat-icon matSuffix color="warn">lock</mat-icon>
          </mat-form-field>

          <!-- Site Name -->
          <mat-form-field appearance="outline" class="full-width">
            <mat-label>Site Name *</mat-label>
            <input matInput formControlName="name" placeholder="Enter site name" required>
            <mat-icon matSuffix>business</mat-icon>
            @if (form.get('name')?.hasError('required') && submitted) {
              <mat-error>Site name is required</mat-error>
            }
            @if (form.get('name')?.hasError('minlength') && submitted) {
              <mat-error>Name must be at least 2 characters</mat-error>
            }
          </mat-form-field>

        </div>
      </mat-card-content>
    </mat-card>

    <!-- Address Information Card -->
    <mat-card class="info-card">
      <mat-card-header>
        <mat-card-title>
          <mat-icon>place</mat-icon>
          Address Information
        </mat-card-title>
      </mat-card-header>
      <mat-card-content>
        <div class="form-grid address-grid">
          
          <!-- Address (Full width) -->
          <mat-form-field appearance="outline" class="full-width">
            <mat-label>Address</mat-label>
            <textarea matInput 
                      formControlName="address"
                      rows="2"
                      placeholder="Street address">
            </textarea>
            <mat-icon matSuffix>home</mat-icon>
          </mat-form-field>

          <!-- City, State, ZIP -->
          <mat-form-field appearance="outline">
            <mat-label>City</mat-label>
            <input matInput formControlName="city" placeholder="City">
            <mat-icon matSuffix>location_city</mat-icon>
          </mat-form-field>

          <mat-form-field appearance="outline">
            <mat-label>State</mat-label>
            <input matInput
                   formControlName="state"
                   placeholder="State"
                   [matAutocomplete]="stateAuto">
            <mat-icon matSuffix>map</mat-icon>
            <mat-autocomplete #stateAuto="matAutocomplete">
              @for (state of filteredStates$ | async; track state) {
                <mat-option [value]="state">{{ state }}</mat-option>
              }
            </mat-autocomplete>
          </mat-form-field>

          <mat-form-field appearance="outline">
            <mat-label>ZIP Code</mat-label>
            <input matInput formControlName="zip" placeholder="12345 or 12345-6789">
            <mat-icon matSuffix>markunread_mailbox</mat-icon>
            @if (form.get('zip')?.hasError('pattern') && submitted) {
              <mat-error>Invalid ZIP format (use 12345 or 12345-6789)</mat-error>
            }
          </mat-form-field>

          <!-- County -->
          <mat-form-field appearance="outline">
            <mat-label>County</mat-label>
            <input matInput formControlName="county" placeholder="County">
            <mat-icon matSuffix>terrain</mat-icon>
          </mat-form-field>

        </div>
      </mat-card-content>
    </mat-card>

    <!-- Business Information Card -->
    <mat-card class="info-card">
      <mat-card-header>
        <mat-card-title>
          <mat-icon>business_center</mat-icon>
          Business Information
        </mat-card-title>
      </mat-card-header>
      <mat-card-content>
        <div class="form-grid business-grid">
          
          <mat-form-field appearance="outline">
            <mat-label>Business Unit Number</mat-label>
            <input matInput formControlName="businessUnitNum" placeholder="BU Number">
            <mat-icon matSuffix>account_tree</mat-icon>
          </mat-form-field>

          <mat-form-field appearance="outline">
            <mat-label>District</mat-label>
            <input matInput formControlName="district" placeholder="District">
            <mat-icon matSuffix>location_searching</mat-icon>
          </mat-form-field>

          <mat-form-field appearance="outline">
            <mat-label>Division</mat-label>
            <input matInput formControlName="division" placeholder="Division">
            <mat-icon matSuffix>corporate_fare</mat-icon>
          </mat-form-field>

          <mat-form-field appearance="outline">
            <mat-label>Formerly Known As</mat-label>
            <input matInput formControlName="fka" placeholder="Previous name">
            <mat-icon matSuffix>history</mat-icon>
          </mat-form-field>

          <mat-form-field appearance="outline">
            <mat-label>Square Footage</mat-label>
            <input matInput formControlName="squareFootage"
                   type="number"
                   placeholder="Square feet">
            <mat-icon matSuffix>square_foot</mat-icon>
            @if (form.get('squareFootage')?.hasError('pattern') && submitted) {
              <mat-error>Must be a valid number</mat-error>
            }
          </mat-form-field>

          <mat-form-field appearance="outline">
            <mat-label>Program</mat-label>
            <input matInput formControlName="program" placeholder="Program">
            <mat-icon matSuffix>category</mat-icon>
          </mat-form-field>

        </div>
      </mat-card-content>
    </mat-card>

    <!-- Status & Comments Card -->
    <mat-card class="info-card">
      <mat-card-header>
        <mat-card-title>
          <mat-icon>settings</mat-icon>
          Status & Notes
        </mat-card-title>
      </mat-card-header>
      <mat-card-content>
        
        <!-- Active/Inactive Toggle -->
        <div class="status-section">
          <div class="toggle-container">
            <mat-slide-toggle 
              formControlName="inactive"
              [disabled]="savedAsInactiveInDb && currentSite.inactive"
              (change)="inactiveToggleChanged($event)">
              <span class="toggle-label" [class.active]="!form.get('inactive')?.value" 
                                        [class.inactive]="form.get('inactive')?.value">
                @if (!form.get('inactive')?.value) {
                  <mat-icon>check_circle</mat-icon>
                  Active
                } @else {
                  <mat-icon>cancel</mat-icon>
                  Inactive
                }
              </span>
            </mat-slide-toggle>
          </div>
        </div>

        <mat-divider></mat-divider>

        <!-- Comments Section -->
        <div class="comments-section">
          <mat-form-field appearance="outline" class="full-width">
            <mat-label>Comments & Notes</mat-label>
            <textarea matInput 
                      formControlName="comments"
                      rows="4"
                      placeholder="Additional notes about this site">
            </textarea>
            <mat-icon matSuffix>note</mat-icon>
          </mat-form-field>
        </div>

      </mat-card-content>
    </mat-card>

    <!-- Inactive Site Warning -->
    @if (savedAsInactiveInDb && currentSite.inactive) {
      <mat-card class="warning-card">
        <mat-card-content>
          <div class="warning-message">
            <mat-icon color="warn">warning</mat-icon>
            <span>You cannot save because this is an INACTIVE site. Contact Helpdesk if you need to make changes.</span>
          </div>
        </mat-card-content>
      </mat-card>
    }

    <!-- Validation Messages -->
    @if (!isFormValid() && submitted) {
      <mat-card class="validation-card">
        <mat-card-content>
          <div class="validation-message">
            <mat-icon color="warn">warning</mat-icon>
            <span>{{ getValidationMessage() }}</span>
          </div>
        </mat-card-content>
      </mat-card>
    }

  </form>
</div>

